import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  Download,
  Trash2,
  Shield,
  Cookie,
  User as UserIcon,
  AlertTriangle,
  Loader2,
  Pencil,
} from "lucide-react";

export default function Settings() {
  const { t } = useI18n();
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState(user?.name || "");
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  if (!user) return null;

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/user/data-export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/json" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `neuracom-data-${user.user_id}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(t("settings.export_success"));
    } catch (e) {
      toast.error(t("settings.export_failed"));
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (confirmEmail.trim().toLowerCase() !== user.email.toLowerCase()) {
      toast.error(t("settings.delete_email_mismatch"));
      return;
    }
    setDeleting(true);
    try {
      await api.post("/user/delete-account", { confirm_email: confirmEmail.trim() });
      toast.success(t("settings.delete_success"));
      await logout();
      navigate("/");
    } catch (e) {
      toast.error(t("settings.delete_failed"));
      setDeleting(false);
    }
  };

  const resetCookieConsent = () => {
    try {
      localStorage.removeItem("neuracom_consent_v1");
      toast.success(t("settings.cookies_reset_success"));
      setTimeout(() => window.location.reload(), 600);
    } catch (e) { /* ignore */ }
  };

  const handleEditSubmit = async (e) => {
    e?.preventDefault?.();
    if (!editName.trim() && !newPw) {
      toast.error(t("settings.edit_nothing_to_update"));
      return;
    }
    if (newPw && newPw.length < 8) {
      toast.error(t("settings.edit_password_too_short"));
      return;
    }
    if (newPw && user?.has_password !== false && !currentPw) {
      toast.error(t("settings.edit_current_password_required"));
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {};
      if (editName.trim() && editName.trim() !== (user.name || "")) payload.name = editName.trim();
      if (newPw) {
        payload.new_password = newPw;
        if (currentPw) payload.current_password = currentPw;
      }
      await api.patch("/user/profile", payload);
      await refresh();
      toast.success(t("settings.edit_success"));
      setEditOpen(false);
      setCurrentPw("");
      setNewPw("");
    } catch (err) {
      const code = err?.response?.data?.detail?.error_code;
      const msg =
        code === "INVALID_CURRENT_PASSWORD" ? t("settings.edit_invalid_password") :
        code === "NOTHING_TO_UPDATE" ? t("settings.edit_nothing_to_update") :
        t("settings.edit_failed");
      toast.error(msg);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="px-4 py-8 sm:px-6 lg:px-12 lg:py-10" data-testid="settings-page">
      <header className="mb-8">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#9ACD32]">{t("settings.pretitle")}</p>
        <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
          {t("settings.title")}
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-300 sm:text-base">{t("settings.subtitle")}</p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Profile card */}
        <Card className="border-slate-800 bg-[#0A0C1F] p-6" data-testid="settings-profile-card">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#9ACD32]/15 text-[#9ACD32]">
              <UserIcon className="h-4 w-4" />
            </span>
            <div className="flex-1">
              <h2 className="font-display text-lg font-bold text-white">{t("settings.profile_title")}</h2>
              <p className="mt-1 text-sm text-slate-400">{t("settings.profile_desc")}</p>
              <dl className="mt-5 grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500">{t("settings.email")}</dt>
                  <dd className="mt-1 font-medium text-slate-200" data-testid="settings-email">{user.email}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500">{t("settings.name")}</dt>
                  <dd className="mt-1 font-medium text-slate-200" data-testid="settings-name">{user.name || "—"}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500">{t("settings.plan")}</dt>
                  <dd className="mt-1 font-medium text-slate-200 uppercase">{user.subscription_tier}</dd>
                </div>
                <div>
                  <dt className="text-xs uppercase tracking-wider text-slate-500">{t("settings.locale")}</dt>
                  <dd className="mt-1 font-medium text-slate-200 uppercase">{user.locale}</dd>
                </div>
              </dl>
              <Button
                onClick={() => { setEditName(user.name || ""); setCurrentPw(""); setNewPw(""); setEditOpen(true); }}
                variant="outline"
                className="mt-5 rounded-full border-slate-700 text-slate-200 hover:bg-slate-800"
                data-testid="settings-edit-profile-btn"
              >
                <Pencil className="mr-2 h-4 w-4" />
                {t("settings.edit_profile_btn")}
              </Button>
            </div>
          </div>
        </Card>

        {/* Cookies card */}
        <Card className="border-slate-800 bg-[#0A0C1F] p-6" data-testid="settings-cookies-card">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#9ACD32]/15 text-[#9ACD32]">
              <Cookie className="h-4 w-4" />
            </span>
            <div className="flex-1">
              <h2 className="font-display text-lg font-bold text-white">{t("settings.cookies_title")}</h2>
              <p className="mt-1 text-sm text-slate-400">{t("settings.cookies_desc")}</p>
              <Button
                onClick={resetCookieConsent}
                variant="outline"
                className="mt-5 rounded-full border-slate-700 text-slate-200 hover:bg-slate-800"
                data-testid="settings-cookies-reset-btn"
              >
                {t("settings.cookies_reset_btn")}
              </Button>
            </div>
          </div>
        </Card>

        {/* Data export card */}
        <Card className="border-slate-800 bg-[#0A0C1F] p-6" data-testid="settings-export-card">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#9ACD32]/15 text-[#9ACD32]">
              <Download className="h-4 w-4" />
            </span>
            <div className="flex-1">
              <h2 className="font-display text-lg font-bold text-white">{t("settings.export_title")}</h2>
              <p className="mt-1 text-sm text-slate-400">{t("settings.export_desc")}</p>
              <Button
                onClick={handleExport}
                disabled={exporting}
                className="mt-5 rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="settings-export-btn"
              >
                {exporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                {t("settings.export_btn")}
              </Button>
            </div>
          </div>
        </Card>

        {/* Privacy info */}
        <Card className="border-slate-800 bg-[#0A0C1F] p-6">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#9ACD32]/15 text-[#9ACD32]">
              <Shield className="h-4 w-4" />
            </span>
            <div className="flex-1">
              <h2 className="font-display text-lg font-bold text-white">{t("settings.rights_title")}</h2>
              <p className="mt-1 text-sm text-slate-400">{t("settings.rights_desc")}</p>
              <ul className="mt-4 space-y-1.5 text-sm text-slate-300">
                <li>• {t("settings.right_access")}</li>
                <li>• {t("settings.right_portability")}</li>
                <li>• {t("settings.right_erasure")}</li>
                <li>• {t("settings.right_rectification")}</li>
                <li>• {t("settings.right_object")}</li>
              </ul>
            </div>
          </div>
        </Card>

        {/* Danger zone */}
        <Card className="border-red-900/40 bg-red-950/10 p-6 lg:col-span-2" data-testid="settings-danger-card">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-red-500/15 text-red-400">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div className="flex-1">
              <h2 className="font-display text-lg font-bold text-red-300">{t("settings.danger_title")}</h2>
              <p className="mt-1 text-sm text-slate-300">{t("settings.danger_desc")}</p>
              {!confirmOpen ? (
                <Button
                  onClick={() => setConfirmOpen(true)}
                  variant="outline"
                  className="mt-5 rounded-full border-red-800 bg-red-950/30 text-red-300 hover:bg-red-900/50 hover:text-red-200"
                  data-testid="settings-delete-open-btn"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t("settings.delete_btn")}
                </Button>
              ) : (
                <div className="mt-5 space-y-3 rounded-lg border border-red-900/40 bg-[#0A0C1F] p-4">
                  <p className="text-sm font-semibold text-red-200">{t("settings.delete_confirm_title")}</p>
                  <p className="text-xs text-slate-400">
                    {t("settings.delete_confirm_desc", { email: user.email })}
                  </p>
                  <Input
                    placeholder={user.email}
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    className="border-red-900/40 bg-slate-950 text-sm text-slate-100"
                    data-testid="settings-delete-confirm-input"
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      onClick={handleDelete}
                      disabled={deleting || confirmEmail.trim().toLowerCase() !== user.email.toLowerCase()}
                      className="rounded-full bg-red-600 font-bold text-white hover:bg-red-700"
                      data-testid="settings-delete-confirm-btn"
                    >
                      {deleting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                      {t("settings.delete_confirm_btn")}
                    </Button>
                    <Button
                      onClick={() => { setConfirmOpen(false); setConfirmEmail(""); }}
                      variant="outline"
                      className="rounded-full border-slate-700 text-slate-300"
                      data-testid="settings-delete-cancel-btn"
                    >
                      {t("common.cancel")}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>

      {/* Edit profile dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="border-slate-800 bg-[#0A0C1F] text-slate-100 sm:max-w-md" data-testid="settings-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{t("settings.edit_title")}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {t("settings.edit_desc")}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-500">{t("settings.name")}</label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder={user.name || t("settings.name")}
                className="mt-1 border-slate-700 bg-slate-950 text-slate-100"
                data-testid="settings-edit-name-input"
                autoComplete="name"
              />
            </div>
            <div className="rounded-lg border border-slate-800 p-3">
              <p className="text-xs uppercase tracking-wider text-slate-500">{t("settings.change_password")}</p>
              <p className="mt-1 text-[11px] text-slate-500">{t("settings.change_password_hint")}</p>
              <Input
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                placeholder={t("settings.current_password")}
                className="mt-2 border-slate-700 bg-slate-950 text-slate-100"
                data-testid="settings-edit-current-pw"
                autoComplete="current-password"
              />
              <Input
                type="password"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                placeholder={t("settings.new_password")}
                className="mt-2 border-slate-700 bg-slate-950 text-slate-100"
                data-testid="settings-edit-new-pw"
                autoComplete="new-password"
                minLength={8}
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditOpen(false)}
                className="rounded-full border-slate-700 text-slate-300"
                data-testid="settings-edit-cancel-btn"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={savingEdit}
                className="rounded-full bg-[#9ACD32] font-bold text-[#05073B] hover:bg-[#A8DF38]"
                data-testid="settings-edit-save-btn"
              >
                {savingEdit ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {t("settings.edit_save_btn")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
